export type ProjectListItem = {
  id: number;
  internal_id: string;
  name: string;
  status: string;
  project_group: { id: number; name: string } | null;
  harvest_total_hours?: number;
  harvest_hours_left?: number;
};

export type DeveloperListItem = {
  id: number;
  email: string;
  name: string;
  hours_available?: number;
  hours_scheduled?: number;
};

export type WeeklyPlannerDeveloper = {
  id: number;
  email: string;
  name: string;
  role: string;
  available_hours?: number;
  available_hours_manually_set?: boolean;
  available_hours_updated_at?: string | null;
  scheduled_hours?: number;
  low_availability: boolean;
  schedule_status: "over" | "under" | "balanced";
};

export type WeeklyPlannerCell = {
  user_id: number;
  estimated_hours: number;
  actual_hours: number;
  allocation_updated_at: string | null;
  allocation_updated_by_id: number | null;
};

export type WeeklyPlannerRepositoryLink = {
  provider: string;
  name: string;
  url: string | null;
  open_prs_url: string | null;
  recent_commits_url: string | null;
  pull_requests?: Array<{
    title: string;
    description: string;
    url: string | null;
    status: string;
    updated_at: string | null;
  }>;
  commits?: Array<{
    title: string;
    description: string;
    url: string | null;
    author: string;
    committed_at: string | null;
  }>;
};

export type WeeklyPlannerRow = {
  row_kind: "group_heading" | "project" | "roll_up_group";
  row_entity_id: number;
  row_label: string;
  archived?: boolean;
  total_hours?: number | null;
  hours_left?: number | null;
  total_planned_hours?: number;
  due_date?: string | null;
  task_notes?: string;
  invoicing_notes?: string;
  internal_id?: string;
  start_date?: string | null;
  projected_end_date?: string | null;
  project_owner_id?: number | null;
  project_owner_email?: string | null;
  project_manager_id?: number | null;
  project_manager_email?: string | null;
  frontend_lead_id?: number | null;
  frontend_lead_email?: string | null;
  backend_lead_id?: number | null;
  backend_lead_email?: string | null;
  included_harvest_projects?: string[];
  harvest_url?: string | null;
  asana_url?: string | null;
  repository_links?: WeeklyPlannerRepositoryLink[];
  team_hours?: number;
  my_hours?: number;
  team_hours_actual?: number;
  my_hours_actual?: number;
  can_edit_all_hours?: boolean;
  editable_user_ids?: number[];
  can_edit_metadata?: boolean;
  cells?: WeeklyPlannerCell[];
};

export type WeeklyPlannerPayload = {
  week_start_on: string;
  current_user_id?: number | null;
  editable: boolean;
  include_archived?: boolean;
  permissions?: {
    can_edit_project_metadata?: boolean;
  };
  leader_options?: Array<{ id: number; email: string }>;
  developers: WeeklyPlannerDeveloper[];
  rows: WeeklyPlannerRow[];
};

export type IntegrationOption = {
  id: string;
  name: string;
  url?: string;
  provider?: string;
};

export type IntegrationCredentialField = {
  key: string;
  label: string;
  required: boolean;
  secret: boolean;
  input_type?: "text" | "boolean";
};

export type IntegrationProviderSetting = {
  provider: string;
  label: string;
  config_method: string;
  required_config_method: string;
  enabled: boolean;
  credential_fields: IntegrationCredentialField[];
  credentials: Record<string, string | boolean>;
  oauth_data: Record<string, string>;
  oauth_connected: boolean;
  oauth_connected_at: string | null;
  oauth_last_refreshed_at: string | null;
  configured: boolean;
  configured_at: string | null;
};

export type ApiTokenListItem = {
  id: number;
  name: string;
  created_at: string;
  last_used_at: string | null;
  expires_at: string | null;
  revoked_at: string | null;
  active: boolean;
};

type ApiErrorResponse = {
  error?: {
    message?: string;
    messages?: string[];
    type?: string;
  };
};

async function apiErrorFromResponse(response: Response, fallbackMessage: string): Promise<Error> {
  const data = (await response.json().catch(() => null)) as ApiErrorResponse | null;
  const messages = data?.error?.messages;
  const message =
    Array.isArray(messages) && messages.length > 0
      ? messages.join(", ")
      : data?.error?.message || fallbackMessage;
  return new Error(message);
}

export async function searchIntegrationOptions(
  provider: string,
  query: string,
): Promise<IntegrationOption[]> {
  const searchParams = new URLSearchParams({ q: query });

  const response = await fetch(
    `/api/v1/integrations/${encodeURIComponent(provider)}/options?${searchParams.toString()}`,
    { cache: "no-store" },
  );
  if (!response.ok)
    throw await apiErrorFromResponse(response, "Failed to load integration options");

  const data = (await response.json()) as { options: IntegrationOption[] };
  return data.options;
}

export async function fetchIntegrationSettings(): Promise<IntegrationProviderSetting[]> {
  const response = await fetch("/api/v1/integration_settings", { cache: "no-store" });
  if (!response.ok)
    throw await apiErrorFromResponse(response, "Failed to load integration settings");

  const data = (await response.json()) as { providers: IntegrationProviderSetting[] };
  return data.providers;
}

export async function updateIntegrationSetting(
  provider: string,
  payload: {
    enabled: boolean;
    config_method: string;
    credentials: Record<string, string | boolean>;
  },
): Promise<IntegrationProviderSetting> {
  const csrfToken = document.querySelector<HTMLMetaElement>("meta[name='csrf-token']")?.content;
  const response = await fetch(`/api/v1/integration_settings/${encodeURIComponent(provider)}`, {
    method: "PUT",
    credentials: "same-origin",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      ...(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
    },
    body: JSON.stringify({ integration_setting: payload }),
  });

  if (!response.ok) {
    throw await apiErrorFromResponse(response, "Failed to save integration settings");
  }

  const data = (await response.json()) as { provider: IntegrationProviderSetting };
  return data.provider;
}

export async function startIntegrationOauth(provider: string): Promise<string> {
  const csrfToken = document.querySelector<HTMLMetaElement>("meta[name='csrf-token']")?.content;
  const response = await fetch(
    `/api/v1/integration_settings/${encodeURIComponent(provider)}/connect`,
    {
      method: "POST",
      credentials: "same-origin",
      headers: {
        Accept: "application/json",
        ...(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
      },
    },
  );

  if (!response.ok) {
    throw await apiErrorFromResponse(response, "Failed to start OAuth connection");
  }

  const data = (await response.json()) as { authorization_url: string };
  return data.authorization_url;
}

export async function disconnectIntegrationOauth(provider: string): Promise<void> {
  const csrfToken = document.querySelector<HTMLMetaElement>("meta[name='csrf-token']")?.content;
  const response = await fetch(
    `/api/v1/integration_settings/${encodeURIComponent(provider)}/disconnect`,
    {
      method: "POST",
      credentials: "same-origin",
      headers: {
        Accept: "application/json",
        ...(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
      },
    },
  );

  if (!response.ok) {
    throw await apiErrorFromResponse(response, "Failed to disconnect OAuth provider");
  }
}

export async function refreshIntegrationOauth(provider: string): Promise<void> {
  const csrfToken = document.querySelector<HTMLMetaElement>("meta[name='csrf-token']")?.content;
  const response = await fetch(
    `/api/v1/integration_settings/${encodeURIComponent(provider)}/refresh`,
    {
      method: "POST",
      credentials: "same-origin",
      headers: {
        Accept: "application/json",
        ...(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
      },
    },
  );

  if (!response.ok) {
    throw await apiErrorFromResponse(response, "Failed to refresh OAuth token");
  }
}

export async function testIntegrationConnection(
  provider: string,
): Promise<{ provider: string; ok: boolean; message: string }> {
  const csrfToken = document.querySelector<HTMLMetaElement>("meta[name='csrf-token']")?.content;
  const response = await fetch(
    `/api/v1/integration_settings/${encodeURIComponent(provider)}/test`,
    {
      method: "POST",
      credentials: "same-origin",
      headers: {
        Accept: "application/json",
        ...(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
      },
    },
  );

  if (!response.ok) {
    throw await apiErrorFromResponse(response, "Failed to test integration connection");
  }

  return (await response.json()) as { provider: string; ok: boolean; message: string };
}

export async function fetchWeeklyPlanner(
  weekStartOn: string,
  view: "weekly_planner" | "my_projects" = "weekly_planner",
  includeArchived = false,
): Promise<WeeklyPlannerPayload> {
  const searchParams = new URLSearchParams({
    week_start_on: weekStartOn,
    view,
  });
  if (includeArchived) searchParams.set("include_archived", "true");
  const response = await fetch(`/api/v1/weekly_planner?${searchParams.toString()}`, {
    cache: "no-store",
    credentials: "same-origin",
    headers: { Accept: "application/json" },
  });
  if (!response.ok) throw await apiErrorFromResponse(response, "Failed to load weekly planner");

  const data = (await response.json()) as { planner: WeeklyPlannerPayload };
  return data.planner;
}

/** Retries transient failures (e.g. proxy 502) with 1s then 2s backoff. */
export async function fetchWeeklyPlannerWithRetry(
  weekStartOn: string,
  view: "weekly_planner" | "my_projects" = "weekly_planner",
  includeArchived = false,
): Promise<WeeklyPlannerPayload> {
  let lastError: unknown;
  for (let attempt = 0; attempt < 3; attempt++) {
    try {
      return await fetchWeeklyPlanner(weekStartOn, view, includeArchived);
    } catch (error) {
      lastError = error;
      if (attempt < 2) {
        await new Promise((resolve) => setTimeout(resolve, attempt === 0 ? 1000 : 2000));
      }
    }
  }
  throw lastError instanceof Error ? lastError : new Error("Failed to load weekly planner");
}

export type RepositoryActivityResponse = {
  pull_requests: NonNullable<WeeklyPlannerRepositoryLink["pull_requests"]>;
  commits: NonNullable<WeeklyPlannerRepositoryLink["commits"]>;
};

export async function fetchRepositoryActivity(
  provider: string,
  repositoryUrl: string,
): Promise<RepositoryActivityResponse> {
  const searchParams = new URLSearchParams({
    provider,
    repository_url: repositoryUrl,
  });
  const response = await fetch(
    `/api/v1/weekly_planner/repository_activity?${searchParams.toString()}`,
    {
      cache: "no-store",
      credentials: "same-origin",
      headers: { Accept: "application/json" },
    },
  );
  if (!response.ok) {
    const data = (await response.json().catch(() => null)) as {
      error?: { message?: string };
    } | null;
    throw new Error(data?.error?.message || "Failed to load repository activity");
  }
  return (await response.json()) as RepositoryActivityResponse;
}

export async function fetchApiTokens(): Promise<ApiTokenListItem[]> {
  const response = await fetch("/api/v1/api_tokens", {
    cache: "no-store",
    credentials: "same-origin",
    headers: { Accept: "application/json" },
  });

  if (!response.ok) throw await apiErrorFromResponse(response, "Failed to load API tokens");
  const data = (await response.json()) as { tokens: ApiTokenListItem[] };
  return data.tokens;
}

export async function createApiToken(payload: {
  name: string;
  expires_at?: string | null;
}): Promise<{ token: ApiTokenListItem & { plaintext_token: string } }> {
  const csrfToken = document.querySelector<HTMLMetaElement>("meta[name='csrf-token']")?.content;
  const response = await fetch("/api/v1/api_tokens", {
    method: "POST",
    credentials: "same-origin",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      ...(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
    },
    body: JSON.stringify({
      api_token: {
        name: payload.name,
        expires_at: payload.expires_at || null,
      },
    }),
  });

  if (!response.ok) {
    throw await apiErrorFromResponse(response, "Failed to create API token");
  }

  return (await response.json()) as { token: ApiTokenListItem & { plaintext_token: string } };
}

export async function revokeApiToken(id: number): Promise<void> {
  const csrfToken = document.querySelector<HTMLMetaElement>("meta[name='csrf-token']")?.content;
  const response = await fetch(`/api/v1/api_tokens/${id}`, {
    method: "DELETE",
    credentials: "same-origin",
    headers: {
      Accept: "application/json",
      ...(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
    },
  });

  if (!response.ok) throw await apiErrorFromResponse(response, "Failed to revoke API token");
}

export async function saveWeeklyPlannerAllocation(payload: {
  week_start_on: string;
  row_kind: "project" | "roll_up_group";
  row_entity_id: number;
  user_id: number;
  estimated_hours: number;
  view?: "weekly_planner" | "my_projects";
  include_archived?: boolean;
  client_request_id?: string;
}): Promise<WeeklyPlannerPayload> {
  const csrfToken = document.querySelector<HTMLMetaElement>("meta[name='csrf-token']")?.content;
  const response = await fetch("/api/v1/weekly_planner/allocation", {
    method: "PUT",
    credentials: "same-origin",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      ...(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
    },
    body: JSON.stringify({
      client_request_id: payload.client_request_id,
      week_start_on: payload.week_start_on,
      view: payload.view,
      include_archived: payload.include_archived,
      allocation: {
        row_kind: payload.row_kind,
        row_entity_id: payload.row_entity_id,
        user_id: payload.user_id,
        estimated_hours: payload.estimated_hours,
      },
    }),
  });

  if (!response.ok) {
    const data = (await response.json().catch(() => null)) as ApiErrorResponse | null;
    const messages = data?.error?.messages;
    const message =
      Array.isArray(messages) && messages.length > 0
        ? messages.join(", ")
        : data?.error?.message || "Failed to save planner allocation";
    const error = new Error(message) as Error & { code?: string; details?: unknown };
    error.code = data?.error?.type;
    error.details = data?.error;
    throw error;
  }

  const data = (await response.json()) as { planner: WeeklyPlannerPayload };
  return data.planner;
}

export async function saveWeeklyPlannerAvailableHours(payload: {
  week_start_on: string;
  user_id: number;
  hours_available: number;
  view?: "weekly_planner" | "my_projects";
  include_archived?: boolean;
  client_request_id?: string;
}): Promise<WeeklyPlannerPayload> {
  const csrfToken = document.querySelector<HTMLMetaElement>("meta[name='csrf-token']")?.content;
  const response = await fetch("/api/v1/weekly_planner/available_hours", {
    method: "PUT",
    credentials: "same-origin",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      ...(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
    },
    body: JSON.stringify({
      client_request_id: payload.client_request_id,
      week_start_on: payload.week_start_on,
      view: payload.view,
      include_archived: payload.include_archived,
      available_hours: {
        user_id: payload.user_id,
        hours_available: payload.hours_available,
      },
    }),
  });

  if (!response.ok) {
    const data = await response.json().catch(() => null);
    const message = data?.error?.message || "Failed to save available hours";
    throw new Error(message);
  }

  const data = (await response.json()) as { planner: WeeklyPlannerPayload };
  return data.planner;
}

export async function saveWeeklyPlannerRowNote(payload: {
  week_start_on: string;
  row_kind: "project" | "roll_up_group";
  row_entity_id: number;
  due_date: string | null;
  task_notes: string;
  invoicing_notes: string;
  view?: "weekly_planner" | "my_projects";
  include_archived?: boolean;
  client_request_id?: string;
}): Promise<WeeklyPlannerPayload> {
  const csrfToken = document.querySelector<HTMLMetaElement>("meta[name='csrf-token']")?.content;
  const response = await fetch("/api/v1/weekly_planner/row_note", {
    method: "PUT",
    credentials: "same-origin",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      ...(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
    },
    body: JSON.stringify({
      client_request_id: payload.client_request_id,
      week_start_on: payload.week_start_on,
      view: payload.view,
      include_archived: payload.include_archived,
      row_note: {
        row_kind: payload.row_kind,
        row_entity_id: payload.row_entity_id,
        due_date: payload.due_date,
        task_notes: payload.task_notes,
        invoicing_notes: payload.invoicing_notes,
      },
    }),
  });

  if (!response.ok) throw await apiErrorFromResponse(response, "Failed to save planner notes");
  const data = (await response.json()) as { planner: WeeklyPlannerPayload };
  return data.planner;
}

export async function saveWeeklyPlannerProjectMetadata(payload: {
  row_entity_id: number;
  projected_end_date?: string | null;
  project_owner_id?: number | null;
  project_manager_id?: number | null;
  frontend_lead_id?: number | null;
  backend_lead_id?: number | null;
  week_start_on?: string;
  view?: "weekly_planner" | "my_projects";
  include_archived?: boolean;
  client_request_id?: string;
}): Promise<WeeklyPlannerPayload> {
  const csrfToken = document.querySelector<HTMLMetaElement>("meta[name='csrf-token']")?.content;
  const response = await fetch("/api/v1/weekly_planner/project_metadata", {
    method: "PUT",
    credentials: "same-origin",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      ...(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
    },
    body: JSON.stringify({
      client_request_id: payload.client_request_id,
      week_start_on: payload.week_start_on,
      view: payload.view,
      include_archived: payload.include_archived,
      project_metadata: {
        row_entity_id: payload.row_entity_id,
        projected_end_date: payload.projected_end_date,
        project_owner_id: payload.project_owner_id,
        project_manager_id: payload.project_manager_id,
        frontend_lead_id: payload.frontend_lead_id,
        backend_lead_id: payload.backend_lead_id,
      },
    }),
  });

  if (!response.ok) throw await apiErrorFromResponse(response, "Failed to save project metadata");
  const data = (await response.json()) as { planner: WeeklyPlannerPayload };
  return data.planner;
}

export async function saveWeeklyPlannerProjectOrder(payload: {
  week_start_on: string;
  view: "weekly_planner" | "my_projects";
  project_group_id: number;
  project_ids: number[];
  include_archived?: boolean;
  client_request_id?: string;
}): Promise<WeeklyPlannerPayload> {
  const csrfToken = document.querySelector<HTMLMetaElement>("meta[name='csrf-token']")?.content;
  const response = await fetch("/api/v1/weekly_planner/project_order", {
    method: "PUT",
    credentials: "same-origin",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      ...(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
    },
    body: JSON.stringify({
      client_request_id: payload.client_request_id,
      week_start_on: payload.week_start_on,
      view: payload.view,
      include_archived: payload.include_archived,
      project_order: {
        project_group_id: payload.project_group_id,
        project_ids: payload.project_ids,
      },
    }),
  });

  if (!response.ok) {
    const data = await response.json().catch(() => null);
    const message = data?.error?.message || "Failed to save project order";
    throw new Error(message);
  }

  const data = (await response.json()) as { planner: WeeklyPlannerPayload };
  return data.planner;
}

export async function saveWeeklyPlannerDeveloperOrder(payload: {
  week_start_on: string;
  view: "weekly_planner" | "my_projects";
  user_ids: number[];
  include_archived?: boolean;
  client_request_id?: string;
}): Promise<WeeklyPlannerPayload> {
  const csrfToken = document.querySelector<HTMLMetaElement>("meta[name='csrf-token']")?.content;
  const response = await fetch("/api/v1/weekly_planner/developer_order", {
    method: "PUT",
    credentials: "same-origin",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      ...(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
    },
    body: JSON.stringify({
      client_request_id: payload.client_request_id,
      week_start_on: payload.week_start_on,
      view: payload.view,
      include_archived: payload.include_archived,
      developer_order: {
        user_ids: payload.user_ids,
      },
    }),
  });

  if (!response.ok) {
    const data = await response.json().catch(() => null);
    const message = data?.error?.message || "Failed to save developer order";
    throw new Error(message);
  }

  const data = (await response.json()) as { planner: WeeklyPlannerPayload };
  return data.planner;
}

export async function syncWeeklyPlanner(
  weekStartOn: string,
  clientRequestId?: string,
  view: "weekly_planner" | "my_projects" = "weekly_planner",
  includeArchived = false,
): Promise<WeeklyPlannerPayload> {
  const csrfToken = document.querySelector<HTMLMetaElement>("meta[name='csrf-token']")?.content;
  const response = await fetch("/api/v1/weekly_planner/sync", {
    method: "POST",
    credentials: "same-origin",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      ...(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
    },
    body: JSON.stringify({
      week_start_on: weekStartOn,
      client_request_id: clientRequestId,
      view,
      include_archived: includeArchived,
    }),
  });

  if (!response.ok) throw await apiErrorFromResponse(response, "Failed to sync weekly planner");
  const data = (await response.json()) as {
    planner: WeeklyPlannerPayload;
    sync: { weeks_synced: string[]; updated_allocations: number; updated_metrics: number };
  };
  return data.planner;
}

export async function syncWeeklyPlannerWithSummary(
  weekStartOn: string,
  clientRequestId?: string,
  view: "weekly_planner" | "my_projects" = "weekly_planner",
  includeArchived = false,
): Promise<{
  planner: WeeklyPlannerPayload;
  sync: { weeks_synced: string[]; updated_allocations: number; updated_metrics: number };
}> {
  const csrfToken = document.querySelector<HTMLMetaElement>("meta[name='csrf-token']")?.content;
  const response = await fetch("/api/v1/weekly_planner/sync", {
    method: "POST",
    credentials: "same-origin",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      ...(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
    },
    body: JSON.stringify({
      week_start_on: weekStartOn,
      client_request_id: clientRequestId,
      view,
      include_archived: includeArchived,
    }),
  });

  if (!response.ok) throw await apiErrorFromResponse(response, "Failed to sync weekly planner");
  return (await response.json()) as {
    planner: WeeklyPlannerPayload;
    sync: { weeks_synced: string[]; updated_allocations: number; updated_metrics: number };
  };
}

export async function copyWeeklyPlannerPreviousWeek(
  weekStartOn: string,
  clientRequestId?: string,
  view: "weekly_planner" | "my_projects" = "weekly_planner",
  includeArchived = false,
): Promise<{
  planner: WeeklyPlannerPayload;
  copy: {
    source_week_start_on: string;
    target_week_start_on: string;
    copied_allocations_count: number;
    copied_row_notes_count: number;
  };
}> {
  const csrfToken = document.querySelector<HTMLMetaElement>("meta[name='csrf-token']")?.content;
  const response = await fetch("/api/v1/weekly_planner/copy_previous_week", {
    method: "POST",
    credentials: "same-origin",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      ...(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
    },
    body: JSON.stringify({
      week_start_on: weekStartOn,
      client_request_id: clientRequestId,
      view,
      include_archived: includeArchived,
    }),
  });

  if (!response.ok) {
    throw await apiErrorFromResponse(response, "Failed to copy previous week");
  }

  return (await response.json()) as {
    planner: WeeklyPlannerPayload;
    copy: {
      source_week_start_on: string;
      target_week_start_on: string;
      copied_allocations_count: number;
      copied_row_notes_count: number;
    };
  };
}
